import React, { useCallback, useEffect, useState, useMemo } from 'react';

import DropdownTreeSelect from 'react-dropdown-tree-select';

import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

import { isV2PermissionsEnabled } from '../../../helpers/featureFlags/isV2PermissionsEnabled';
import { getUserRegions } from '../../../helpers/getUserRegions';
import { DEFAULT_REGIONS_SHORT } from '../helpers/regions';
import { SearchService } from '../services/SearchService';

import './ProviderSearchByGeographyV2.scss';

const placeholder = { placeholder: 'Region, State, Country, City' };

export const ProviderSearchByGeographyV2 = ({
  selectedData,
  onSelectData,
  handleGeographySearchClear,
}) => {
  const selectedDataList = Array.isArray(selectedData) ? selectedData : [];
  const [countriesData, setCountriesData] = useState([]);
  const regionsV2 = useMemo(() => getUserRegions(), []);
  const regions = isV2PermissionsEnabled() ? regionsV2 : DEFAULT_REGIONS_SHORT;

  const getNodePathfromId = useCallback((id = '') => {
    // example of a node id : 'rdts1-1-5-0-0'
    // where rdts means react-dropdown-tree-select (name of the package), which is a base string
    // rdts1 represents the (root + 1) level branch index
    // rest of the symbols (1-5-0-0) represents the path of the selected node starting from (root + 1) level node

    // reversing the path to traverse
    const selectedNodePath = id.split('-').reverse();

    // removing the base string rdts{n}
    selectedNodePath.pop();

    return selectedNodePath;
  }, []);

  const onChangeTreeValue = useCallback((data, key, value) => {
    data[key] = value;
    if (!!data.children && Array.isArray(data.children)) {
      data.children.forEach((_c) => {
        onChangeTreeValue(_c, key, value);
      });
    }
  }, []);

  const onChangeCountriesData = useCallback(
    (data, nodepath = [], key, value = false, applyToTree = false) => {
      if (nodepath.length === 0 || !data) return;
      const index = Number(nodepath.pop());

      if (typeof index !== 'number') return;

      if (nodepath.length === 0) {
        data.children[index][key] = value;
        applyToTree && onChangeTreeValue(data.children[index], key, value);
      } else onChangeCountriesData(data.children[index], nodepath, key, value, applyToTree);
    },
    [onChangeTreeValue],
  );

  const onChange = useCallback(
    (currentNode, selectedNodes) => {
      const normalizedSelectedNodes = Array.isArray(selectedNodes)
        ? selectedNodes
        : Array.isArray(selectedNodes?.allCheckedNodes)
        ? selectedNodes.allCheckedNodes
        : [];

      const selectedCount = Number(
        Number.isFinite(selectedNodes?.count)
          ? selectedNodes.count
          : normalizedSelectedNodes.length,
      );

      localStorage.setItem('selectedOprionsCount', selectedCount);

      const selectedNodeId = currentNode._id;
      const selectedNodeValue = currentNode.checked;

      const selectedNodePath = getNodePathfromId(selectedNodeId);

      onChangeCountriesData(
        { children: countriesData },
        selectedNodePath,
        'checked',
        selectedNodeValue,
        true,
      );

      if (!selectedNodeValue && selectedCount === 0) {
        onChangeCountriesData(
          { children: [{ children: countriesData }] },
          [0],
          'checked',
          false,
          true,
        );
        handleGeographySearchClear();
        onSelectData([]);

        return;
      }

      if (normalizedSelectedNodes.length > 0) {
        onSelectData(normalizedSelectedNodes);

        return;
      }

      // Defensive fallback for inconsistent selectedNodes payload: remove only clicked node.
      const selectedNodeIdPrefix = `${selectedNodeId}-`;
      const fallbackSelectedNodes = selectedDataList.filter((node) => {
        const nodeId = node?._id || '';

        return !(nodeId === selectedNodeId || nodeId.startsWith(selectedNodeIdPrefix));
      });

      onSelectData(fallbackSelectedNodes);
    },
    [
      countriesData,
      getNodePathfromId,
      handleGeographySearchClear,
      onChangeCountriesData,
      onSelectData,
      selectedDataList,
    ],
  );

  const onNodeToggle = useCallback(
    (currentNode) => {
      const selectedNodeId = currentNode._id;
      const selectedNodeValue = currentNode.expanded;
      const selectedNodePath = getNodePathfromId(selectedNodeId);

      onChangeCountriesData(
        { children: countriesData },
        selectedNodePath,
        'expanded',
        selectedNodeValue,
        !selectedNodeValue, // apply the value to entire tree when the parent is collapsed(false)
      );
    },
    [countriesData, getNodePathfromId, onChangeCountriesData],
  );

  const clearSelection = useCallback(() => {
    onChangeCountriesData({ children: [{ children: countriesData }] }, [0], 'checked', false, true);
    handleGeographySearchClear();
    onSelectData([]);
  }, [countriesData, onChangeCountriesData, onSelectData, handleGeographySearchClear]);

  const fetchCountriesByRegion = useCallback(async (regions) => {
    const countriesArray = [];

    try {
      const res = await Promise.all(regions.map((region) => SearchService.getCountries(region)));

      // isDefaultValue
      regions.filter((region, key) => {
        const citiesObj = SearchService.generateSearchDataObject(region, res[key]?.data);

        countriesArray.push(citiesObj);
      });

      setCountriesData(countriesArray);
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    if (regions.length > 0) {
      fetchCountriesByRegion(regions);
    }
  }, [fetchCountriesByRegion, regions]);

  return countriesData ? (
    <div className="regions-search">
      <DropdownTreeSelect
        texts={placeholder}
        data={countriesData}
        onChange={onChange}
        onNodeToggle={onNodeToggle}
        className="regions-tree-select"
        keepOpenOnSelect={true}
        keepTreeOnSearch={true}
        showPartiallySelected={true}
        hierarchical={false}
      />
      {selectedDataList.length > 0 && (
        <div className="clear-all-btn-wrapper">
          <IconButton onClick={clearSelection}>
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </div>
  ) : null;
};
